import { useEffect } from 'react';

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  noIndex?: boolean;
  structuredData?: Record<string, unknown>;
}

/**
 * SEO component that updates document head for each page.
 * Improves search engine indexing and social sharing.
 */
export function SEOHead({
  title = 'MSB Kids Programs',
  description = 'Register for exciting kids programs at MSB. Art, music, sports, coding and more for ages 4-14. Build confidence and make friends!',
  canonicalUrl,
  ogImage = 'https://msbcamps.com/og-image.jpg',
  ogType = 'website',
  noIndex = false,
  structuredData,
}: SEOHeadProps) {
  const fullTitle = title === 'MSB Kids Programs' 
    ? title 
    : `${title} | MSB Kids Programs`;

  useEffect(() => {
    // Update title
    document.title = fullTitle;

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }

    // Update OG tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', fullTitle);
    }

    let ogTypeTag = document.querySelector('meta[property="og:type"]') as HTMLMetaElement | null;
    if (!ogTypeTag) {
      ogTypeTag = document.createElement('meta');
      ogTypeTag.setAttribute('property', 'og:type');
      document.head.appendChild(ogTypeTag);
    }
    ogTypeTag.setAttribute('content', ogType);

    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) {
      ogDesc.setAttribute('content', description);
    }

    const absoluteOgImage = ogImage?.startsWith('http')
      ? ogImage
      : `https://msbcamps.com${ogImage?.startsWith('/') ? '' : '/'}${ogImage ?? ''}`;
    const ogImageTag = document.querySelector('meta[property="og:image"]');
    if (ogImageTag && ogImage) {
      ogImageTag.setAttribute('content', absoluteOgImage);
    }

    // Keep og:url in sync with the canonical URL for accurate social previews
    if (canonicalUrl) {
      let ogUrlTag = document.querySelector('meta[property="og:url"]') as HTMLMetaElement | null;
      if (!ogUrlTag) {
        ogUrlTag = document.createElement('meta');
        ogUrlTag.setAttribute('property', 'og:url');
        document.head.appendChild(ogUrlTag);
      }
      ogUrlTag.setAttribute('content', canonicalUrl);
    }

    // Twitter card mirrors OG values
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) twitterTitle.setAttribute('content', fullTitle);
    const twitterDesc = document.querySelector('meta[name="twitter:description"]');
    if (twitterDesc) twitterDesc.setAttribute('content', description);
    const twitterImage = document.querySelector('meta[name="twitter:image"]');
    if (twitterImage && ogImage) twitterImage.setAttribute('content', absoluteOgImage);

    // Handle canonical URL
    let canonicalTag = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (canonicalUrl) {
      if (!canonicalTag) {
        canonicalTag = document.createElement('link');
        canonicalTag.rel = 'canonical';
        document.head.appendChild(canonicalTag);
      }
      canonicalTag.href = canonicalUrl;
    } else if (canonicalTag) {
      canonicalTag.remove();
    }


    // Handle noindex
    let robotsTag = document.querySelector('meta[name="robots"]') as HTMLMetaElement;
    if (noIndex) {
      if (!robotsTag) {
        robotsTag = document.createElement('meta');
        robotsTag.name = 'robots';
        document.head.appendChild(robotsTag);
      }
      robotsTag.content = 'noindex, nofollow';
    } else if (robotsTag) {
      robotsTag.remove();
    }

    // Handle structured data (JSON-LD)
    let ldJsonScript = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
    if (structuredData) {
      if (!ldJsonScript) {
        ldJsonScript = document.createElement('script');
        ldJsonScript.type = 'application/ld+json';
        document.head.appendChild(ldJsonScript);
      }
      ldJsonScript.textContent = JSON.stringify(structuredData);
    } else if (ldJsonScript) {
      ldJsonScript.remove();
    }

    // Cleanup on unmount
    return () => {
      // Reset to defaults when component unmounts
      document.title = 'MSB Kids Programs - Registration';
    };
  }, [fullTitle, description, canonicalUrl, ogImage, ogType, noIndex, structuredData]);

  return null;
}

// Common structured data templates
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'MSB Kids Programs',
  description: 'Youth programs including after-school care, summer camps, holiday camps, and clubs',
  url: 'https://msbcamps.com',
  logo: 'https://msbcamps.com/favicon.png',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'Customer Service',
    availableLanguage: 'English',
  },
};


export const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'MSB Kids Programs',
  description: 'Youth after-school and camp programs',
  url: 'https://msbcamps.com',
  image: 'https://msbcamps.com/og-image.jpg',
  logo: 'https://msbcamps.com/favicon.png',
  telephone: '+1-602-550-0931',
  email: 'info@msbcamps.com',
  priceRange: '$$',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Phoenix',
    addressRegion: 'AZ',
    addressCountry: 'US',
  },
};

export const contactPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact MSB Kids Programs',
  url: 'https://msbcamps.com/contact',
  description:
    'Contact the MSB Kids Programs team in Phoenix for program questions, tours, and registration help.',
  mainEntity: {
    '@type': 'Organization',
    name: 'MSB Kids Programs',
    url: 'https://msbcamps.com',
    logo: 'https://msbcamps.com/favicon.png',
    image: 'https://msbcamps.com/og-image.jpg',
    email: 'info@msbcamps.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Phoenix',
      addressRegion: 'AZ',
      addressCountry: 'US',
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        name: 'Zack Blinder',
        telephone: '+1-602-550-0931',
        contactType: 'Director',
        areaServed: 'US',
        availableLanguage: 'English',
      },
      {
        '@type': 'ContactPoint',
        name: 'Jedd Blinder',
        telephone: '+1-602-309-7476',
        contactType: 'Director',
        areaServed: 'US',
        availableLanguage: 'English',
      },
      {
        '@type': 'ContactPoint',
        email: 'info@msbcamps.com',
        contactType: 'Customer Service',
        areaServed: 'US',
        availableLanguage: 'English',
      },
    ],
  },
};

/** Build a Service schema entry for an MSB program page. */
export function buildServiceSchema(input: {
  name: string;
  description: string;
  url: string;
  serviceType?: string;
  audience?: string;
}) {
  return {
    '@type': 'Service',
    name: input.name,
    description: input.description,
    url: input.url,
    serviceType: input.serviceType ?? 'Youth program',
    provider: {
      '@type': 'Organization',
      name: 'MSB Camps',
      url: 'https://msbcamps.com',
    },
    areaServed: { '@type': 'City', name: 'Phoenix' },
    audience: {
      '@type': 'PeopleAudience',
      audienceType: input.audience ?? 'Children K-8',
    },
  };
}

/** Build a FAQPage schema from a list of question/answer pairs. */
export function buildFaqSchema(faqs: { question: string; answer: string }[]) {
  return {
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };
}

/** Combine multiple schema nodes into a single JSON-LD @graph document. */
export function buildSchemaGraph(nodes: Record<string, unknown>[]) {
  return { '@context': 'https://schema.org', '@graph': nodes };
}
